import React from 'react'
import moment from 'moment'
import { pgaComTld } from '../../../utils'

export const SUSPENSION_CODES_WITH_TERMINATION = ['GPTP', 'NPDU']
export const TERMINATION_WINDOW_DURATION = 2
export const TERMINATION_WINDOW_UNIT = 'years'

const hasTerminationWindow = (code) => {
  return SUSPENSION_CODES_WITH_TERMINATION.includes(code)
}

export const calculateSuspensionData = (suspension) => {
  if (!suspension || !suspension.startDate) {
    return null
  }

  const startDate = moment(suspension.startDate)
  const endDate = suspension.endDate ? moment(suspension.endDate) : null
  const isActive = !endDate
  const hasTermination = hasTerminationWindow(suspension.code)

  const daysActive = isActive ? moment().diff(startDate, 'days') : null

  const terminationDate = hasTermination && isActive 
    ? startDate.clone().add(TERMINATION_WINDOW_DURATION, TERMINATION_WINDOW_UNIT) 
    : null

  const daysUntilTermination = terminationDate ? terminationDate.diff(moment(), 'days') : null

  const monthsUntilTermination = terminationDate 
    ? Math.floor(daysUntilTermination / 30) 
    : null
  const remainingDays = terminationDate 
    ? daysUntilTermination % 30 
    : null

  return {
    code: suspension.code,
    startDate: suspension.startDate,
    endDate: suspension.endDate,
    note: suspension.note,
    isActive,
    hasTerminationWindow: hasTermination,
    daysActive,
    terminationDate: terminationDate ? terminationDate.format('YYYY-MM-DD') : null,
    daysUntilTermination,
    monthsUntilTermination,
    remainingDays,
    formattedStartDate: startDate.format('MM/DD/YYYY'),
    formattedTerminationDate: terminationDate ? terminationDate.format('MM/DD/YYYY') : null
  }
}

export const formatTimeRemaining = (suspensionData) => {
  if (!suspensionData || !suspensionData.isActive || !suspensionData.terminationDate) {
    return null
  }

  const { monthsUntilTermination, remainingDays } = suspensionData

  if (monthsUntilTermination === 0) {
    return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
  }

  const monthsText = `${monthsUntilTermination} ${monthsUntilTermination === 1 ? 'month' : 'months'}`
  const daysText = remainingDays > 0 
    ? `, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}` 
    : ''

  return `${monthsText}${daysText}`
}

const GptpSuspension = {
  bannerTitle: 'You are suspended for not meeting acceptable progress requirements',
  getBannerDeadlineLabel: (currentLevelName) => `${currentLevelName} Completion Deadline`,
  PopoverMessage: ({ suspensionData }) => (
    <>
      Your progress in this level doesn't meet the rules around acceptable progress. Complete the requirements in this level before {suspensionData?.formattedTerminationDate} in order to avoid termination from the program.
    </>
  )
}

const NpduSuspension = {
  bannerTitle: 'You are suspended for non-payment of dues',
  getBannerDeadlineLabel: () => 'Payment Deadline',
  PopoverMessage: ({ suspensionData }) => (
    <>
      Your membership is suspended due to non-payment of dues. Please <a href={`https://${pgaComTld}/member/account/invoices`} target="_blank" rel="noopener noreferrer">click here</a> to pay your outstanding dues before {suspensionData?.formattedTerminationDate} in order to avoid termination from the program.
    </>
  )
}

const DefaultSuspension = {
  bannerTitle: 'Your membership is currently suspended',
  getBannerDeadlineLabel: () => null,
  PopoverMessage: () => (
    <>
      Your membership is currently suspended. 
      Please contact PGA Membership at <a href="tel:8004742776">(800) 474-2776</a> for more information about your suspension.
    </>
  )
}

const SUSPENSION_TYPES = {
  GPTP: GptpSuspension,
  NPDU: NpduSuspension
}

export const getBannerMessage = (suspensionData, currentLevelName) => {
  if (!suspensionData) return null

  const suspensionType = SUSPENSION_TYPES[suspensionData.code] || DefaultSuspension

  return {
    title: suspensionType.bannerTitle,
    deadline: suspensionData.formattedTerminationDate,
    deadlineLabel: suspensionType.getBannerDeadlineLabel(currentLevelName)
  }
}

export const getSuspensionMessage = (suspensionData, currentLevelName = null) => {
  if (!suspensionData) {
    return null
  }

  const suspensionType = SUSPENSION_TYPES[suspensionData.code] || DefaultSuspension
  const MessageComponent = suspensionType.PopoverMessage
  
  return <MessageComponent suspensionData={suspensionData} currentLevelName={currentLevelName} />
}

export const shouldShowSuspensionWarning = (pgmProgress) => {
  if (!pgmProgress) return false
  return Boolean(pgmProgress.isSuspended && pgmProgress.suspensions?.length > 0)
}

export const getSuspensionFromProgress = (pgmProgress) => {
  if (!shouldShowSuspensionWarning(pgmProgress)) {
    return null
  }
  const suspensionsWithData = pgmProgress.suspensions
    .map(suspension => calculateSuspensionData(suspension))
    .filter(data => data !== null)

  if (suspensionsWithData.length === 0) {
    return null
  }

  const suspensionsWithTermination = suspensionsWithData
    .filter(data => data.hasTerminationWindow && data.terminationDate)
    .sort((a, b) => {
      // Sort by soonest termination date (earlier dates first)
      return new Date(a.terminationDate) - new Date(b.terminationDate)
    })

  if (suspensionsWithTermination.length > 0) {
    return suspensionsWithTermination[0]
  }

  // For suspensions without termination windows, sort by earliest start date
  const sortedByStartDate = suspensionsWithData.sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate)
  })

  return sortedByStartDate[0]
}
